/**
 * HomeScreen — Driver portal home tab.
 * Extracted from DriverApp.jsx (V2-03.12f).
 * Fetches /api/driver/home, displays clock status, daily log, assignment, weekly totals.
 * No logic changes — behavior-identical to inline original.
 */

import React, { useState, useEffect } from 'react';
import { C, FONT, GLASS, GLASS_STRONG, BTN } from '../driver-tokens.js';
import { clearToken, api } from '../auth/driver-session.js';
import type { DriverEmployee } from '../DriverApp.js';

interface HomeScreenProps {
  employee: DriverEmployee | null;
  onLogout: () => void;
  onClockOut: () => void;
  onClockIn: () => void;
}

interface HomeData {
  employee?: DriverEmployee;
  clock_status?: { clocked_in?: boolean; clock_in?: { clock_in_timestamp?: string } };
  daily_log?: { stops?: number; miles?: number; threshold_used?: string | number; stop_bonus?: string | number };
  weekly_totals?: { stops?: number; miles?: number; stop_bonus?: string | number };
  current_assignment?: { terminals?: { name?: string }; assignment_type?: string; started_at?: string };
  error?: string;
}

function HomeScreen({ employee, onLogout, onClockOut, onClockIn }: HomeScreenProps) {
  var [homeData, setHomeData] = useState<HomeData | null>(null);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    api<HomeData>("/api/driver/home").then(function(data) {
      if (data.error) { clearToken(); window.location.reload(); return; }
      setHomeData(data);
      setLoading(false);
    });
  }, []);

  if (loading) return React.createElement("div", { style: { textAlign: "center", padding: 40 } },
    React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 14, color: C.silver } }, "Loading...")
  );

  var d = homeData;
  if (!d) return null;
  var emp = d.employee || {} as DriverEmployee;
  var displayName = emp.nickname || emp.first_name || "Driver";
  var isClockedIn = d.clock_status && d.clock_status.clocked_in;

  return React.createElement("div", { style: { padding: 20, paddingBottom: 100 } },
    // Header
    React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 } },
      React.createElement("div", null,
        React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text } }, "Hey, " + displayName),
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 12, color: C.silver } }, new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }))
      ),
      React.createElement("div", { style: { width: 44, height: 44, borderRadius: "50%", overflow: "hidden", border: "2px solid " + C.gold } },
        emp.photo_url ? React.createElement("img", { src: emp.photo_url, style: { width: "100%", height: "100%", objectFit: "cover" } }) :
        React.createElement("div", { style: { width: "100%", height: "100%", background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT.mono, color: C.textMuted } }, displayName[0])
      )
    ),

    // Clock button
    React.createElement("div", { style: { ...GLASS_STRONG, padding: 24, marginBottom: 16, textAlign: "center", borderRadius: 20 } },
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: isClockedIn ? C.green : C.textMuted, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 } }, isClockedIn ? "CLOCKED IN" : "NOT CLOCKED IN"),
      isClockedIn && d.clock_status?.clock_in?.clock_in_timestamp && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 12, color: C.silver, marginBottom: 12 } },
        "Since " + new Date(d.clock_status.clock_in.clock_in_timestamp).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
      ),
      React.createElement("button", {
        style: { ...BTN, maxWidth: 280, margin: "0 auto", display: "block", background: isClockedIn ? C.red : C.green, color: isClockedIn ? "#fff" : C.navy, fontSize: 18, padding: 20, borderRadius: 14 },
        onClick: function() {
          if (isClockedIn) { onClockOut(); }
          else { onClockIn(); }
        }
      }, isClockedIn ? "Clock Out" : "Clock In")
    ),

    // Today's numbers
    d.daily_log && React.createElement("div", { style: { ...GLASS, padding: 16, marginBottom: 16 } },
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.gold, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 12 } }, "Today's Numbers"),
      React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, textAlign: "center" } },
        React.createElement("div", null,
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 22, color: C.text } }, d.daily_log.stops),
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted } }, "Stops")
        ),
        React.createElement("div", null,
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 22, color: C.text } }, Math.round(d.daily_log.miles || 0)),
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted } }, "Miles")
        ),
        React.createElement("div", null,
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 22, color: C.text } }, d.daily_log.threshold_used || "\u2014"),
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted } }, "Threshold")
        ),
        React.createElement("div", null,
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 22, color: parseFloat(String(d.daily_log.stop_bonus)) > 0 ? C.gold : C.text } }, "$" + parseFloat(String(d.daily_log.stop_bonus || 0)).toFixed(2)),
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted } }, "Bonus")
        )
      )
    ),

    // Assignment
    d.current_assignment && React.createElement("div", { style: { ...GLASS, padding: 16, marginBottom: 16 } },
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.gold, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 } }, "Current Assignment"),
      React.createElement("div", { style: { fontFamily: FONT.body, fontSize: 16, color: C.text } }, d.current_assignment.terminals ? d.current_assignment.terminals.name : "Home"),
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.silver } }, (d.current_assignment.assignment_type || "") + " \u2022 since " + new Date(d.current_assignment.started_at || "").toLocaleDateString())
    ),

    // Weekly totals card
    d.weekly_totals && React.createElement("div", { style: { ...GLASS, padding: 16, marginBottom: 16 } },
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.gold, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 12 } }, "This Week"),
      React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, textAlign: "center" } },
        React.createElement("div", null,
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 22, color: C.text } }, d.weekly_totals.stops),
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted } }, "Stops")
        ),
        React.createElement("div", null,
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 22, color: C.text } }, Math.round(d.weekly_totals.miles || 0)),
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted } }, "Miles")
        ),
        React.createElement("div", null,
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 22, color: parseFloat(String(d.weekly_totals.stop_bonus)) > 0 ? C.gold : C.text } }, "$" + parseFloat(String(d.weekly_totals.stop_bonus || 0)).toFixed(2)),
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted } }, "Bonus")
        )
      )
    ),

    // Sign out
    React.createElement("div", { style: { textAlign: "center", marginTop: 20 } },
      React.createElement("button", { style: { background: "none", border: "none", color: C.textMuted, fontSize: 12, fontFamily: FONT.mono, cursor: "pointer" }, onClick: onLogout }, "Sign Out")
    )
  );
}

export default HomeScreen;
